import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ArtikelGruppeByNrMultiple } from 'formComponents/lookups/ArtikelGruppeByNrMultiple';
import { LagerByIntNrMultiple } from 'formComponents/lookups/LagerByIntNrMultiple';
import { PrimaryButton } from '@fluentui/react';
import { authSelector } from 'store/auth/selector';
import { Lagerstand, LagerstandRef } from './Lagerstand';
import { generateReportPrintUrl } from '../utils';

const MIN_VALUE = 0.0001;

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content max-content max-content 1fr',
        gridRowGap: 10,
        height: 'calc(100vh - 75px)',
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '3fr 2fr max-content max-content max-content max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchLine2: {
        display: 'grid',
        gridTemplateColumns:
            '1fr max-content 1fr max-content 1fr max-content 1fr max-content 1fr max-content 1fr max-content 1fr',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchLine3: {
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr max-content 1fr',
        gridColumnGap: 10,
        marginTop: 0,
    },
    oder: {
        marginTop: 8,
    },
});

interface FilterValues {
    artikelGruppeNrList: number[];
    lagerIntNrList: number[];
    minBestandVon: number;
    bestandBelowMinBestand: boolean;
    verfuegbarBelowMinBestand: boolean;
    bestandVon: number;
    offenBestVon: number;
    offenBestVonD: number;
    offenLiefVon: number;
    offenLiefVonD: number;
    offenResVon: number;
    showOffenBest: boolean;
    showOffenLief: boolean;
    showLagerwert: boolean;
    includeArchiv: boolean;
}

const defaultFilterValues: FilterValues = {
    artikelGruppeNrList: [],
    lagerIntNrList: [],
    minBestandVon: MIN_VALUE,
    bestandBelowMinBestand: false,
    verfuegbarBelowMinBestand: false,
    bestandVon: MIN_VALUE,
    offenBestVon: MIN_VALUE,
    offenBestVonD: MIN_VALUE,
    offenLiefVon: MIN_VALUE,
    offenLiefVonD: MIN_VALUE,
    offenResVon: MIN_VALUE,
    showOffenBest: true,
    showOffenLief: true,
    showLagerwert: true,
    includeArchiv: false,
};

export const LagerstandView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>({
        ...defaultFilterValues,
    });

    const lagerstandRef = React.useRef<LagerstandRef | null>(null);

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine1}>
                <ArtikelGruppeByNrMultiple
                    values={filterValues.artikelGruppeNrList}
                    disabled={false}
                    label="Artikelgruppe(n)"
                    includeArchiv={filterValues.includeArchiv}
                    allowEmpty
                    renderLabel="placeholder"
                    onChange={(newValues): void => {
                        setFilterValues({
                            ...filterValues,
                            artikelGruppeNrList: newValues,
                        });
                    }}
                />

                <LagerByIntNrMultiple
                    values={filterValues.lagerIntNrList}
                    disabled={false}
                    label="Lager"
                    allowEmpty
                    renderLabel="placeholder"
                    onChange={(newValues): void => {
                        setFilterValues({
                            ...filterValues,
                            lagerIntNrList: newValues,
                        });
                    }}
                />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Archivierte Artikel"
                        checked={filterValues.includeArchiv}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                includeArchiv: checked || false,
                            });
                        }}
                    />
                </div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Offene Best/Lief anzeigen"
                        checked={filterValues.showOffenBest}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                showOffenBest: checked || false,
                                showOffenLief: checked || false,
                            });
                        }}
                    />
                </div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Lagerwert anzeigen"
                        checked={filterValues.showLagerwert}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                showLagerwert: checked || false,
                            });
                        }}
                    />
                </div>
                {auth.rolle === 'ID' || auth.rolle === 'GF' ? (
                    <PrimaryButton onClick={() => lagerstandRef?.current?.downloadXlsx()} text="Excel" />
                ) : (
                    <div />
                )}

                <PrimaryButton
                    onClick={() => {
                        window.open(
                            generateReportPrintUrl('lagerstand', {
                                artikelGruppeNrList: filterValues.artikelGruppeNrList.join(','),
                                lagerIntNrList: filterValues.lagerIntNrList.join(','),
                                includeArchiv: filterValues.includeArchiv,
                                minBestandVon: filterValues.minBestandVon,
                                bestandBelowMinBestand: filterValues.bestandBelowMinBestand,
                                verfuegbarBelowMinBestand: filterValues.verfuegbarBelowMinBestand,
                                bestandVon: filterValues.bestandVon,
                                offenBestVon: filterValues.offenBestVon,
                                offenBestVonD: filterValues.offenBestVonD,
                                offenLiefVon: filterValues.offenLiefVon,
                                offenLiefVonD: filterValues.offenLiefVonD,
                                offenRes: filterValues.offenResVon,
                                showOffenBest: filterValues.showOffenBest,
                                showOffenLief: filterValues.showOffenLief,
                                showLagerwert: filterValues.showLagerwert,
                            }),
                        );
                    }}
                    text="Drucken"
                />
            </div>
            <div className={styles.searchLine2}>
                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Bestand > 0"
                        checked={filterValues.bestandVon > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                bestandVon: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>

                <div className={styles.oder}>ODER</div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Mind. Bestand > 0"
                        checked={filterValues.minBestandVon > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                minBestandVon: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>

                <div className={styles.oder}>ODER</div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Offene Best. > 0"
                        checked={filterValues.offenBestVon > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                offenBestVon: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>

                <div className={styles.oder}>ODER</div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Offene Best. (D) > 0"
                        checked={filterValues.offenBestVonD > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                offenBestVonD: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>

                <div className={styles.oder}>ODER</div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Offene Lief. > 0"
                        checked={filterValues.offenLiefVon > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                offenLiefVon: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>

                <div className={styles.oder}>ODER</div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Offene Lief. (D) > 0"
                        checked={filterValues.offenLiefVonD > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                offenLiefVonD: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>

                <div className={styles.oder}>ODER</div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Offene Res. > 0"
                        checked={filterValues.offenResVon > 0}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                offenResVon: checked ? MIN_VALUE : 0,
                            });
                        }}
                    />
                </div>
            </div>

            <div className={styles.searchLine3}>
                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Bestand < Mind. Bestand"
                        checked={filterValues.bestandBelowMinBestand}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                bestandBelowMinBestand: !!checked,
                            });
                        }}
                    />
                </div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Verfügbar < Mind. Bestand"
                        checked={filterValues.verfuegbarBelowMinBestand}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                verfuegbarBelowMinBestand: !!checked,
                            });
                        }}
                    />
                </div>

                <div />
            </div>

            <Lagerstand
                ref={lagerstandRef}
                artikelGruppeNrList={debouncedFilterValues.artikelGruppeNrList}
                lagerIntNrList={debouncedFilterValues.lagerIntNrList}
                includeArchiv={debouncedFilterValues.includeArchiv}
                minBestandVon={debouncedFilterValues.minBestandVon}
                bestandBelowMinBestand={debouncedFilterValues.bestandBelowMinBestand}
                verfuegbarBelowMinBestand={debouncedFilterValues.verfuegbarBelowMinBestand}
                bestandVon={debouncedFilterValues.bestandVon}
                offenBestVon={debouncedFilterValues.offenBestVon}
                offenBestVonD={debouncedFilterValues.offenBestVonD}
                offenLiefVon={debouncedFilterValues.offenLiefVon}
                offenLiefVonD={debouncedFilterValues.offenLiefVonD}
                offenResVon={debouncedFilterValues.offenResVon}
                showOffenBest={debouncedFilterValues.showOffenBest}
                showOffenLief={debouncedFilterValues.showOffenLief}
                showLagerwert={debouncedFilterValues.showLagerwert}
            />
        </div>
    );
};
